import {
  useState,
  createRef,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  ReactElement,
} from 'react';
import { formatNumber } from '@belong/common';
import { IconClose } from '@belong/icons';
import { ModalBase, BulletList, Text, Dropdown, Image } from '@belong/ui';
import clsx from 'clsx';
import { UnitListingDetailsModel } from 'src/api/models';
import { useOnScreen } from 'src/features/homeowners/hooks/use-on-screen';
import {
  getBathRoomString,
  getFormattedBeds,
} from 'src/features/listing-page/utils';
import { getSection } from '../../utils';
import type { Section as SectionType } from '../../utils';
import { ApplyButton } from '../apply-button/apply-button';
import { getMediaFeatures, processHomeFeatures } from '../home-features/utils';
import { RentAmount } from '../rent-amount/rent-amount';
import { GalleryCarousel } from './gallery-carousel';
import { GalleryGrid } from './gallery-grid';
import styles from './gallery.module.css';

const dedupKey = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    // `!`: `split` always yields at least one element.
    return url.split('?')[0]!;
  }
};

type GalleryProps = {
  home: UnitListingDetailsModel;
  mediaSections: SectionType[];
  section?: string;
  onDismiss: () => void;
};

/** One photo of a feature, as `getMediaFeatures` decorates it. */
type FeatureMedia = ReturnType<
  typeof getMediaFeatures
>[number]['media'][number];

/**
 * A feature category rendered as its own gallery section. Structurally matches
 * the `Section`s built from spaces, so the two lists can be concatenated.
 */
type MediaFeatureSection = {
  id: string;
  type: string;
  media: FeatureMedia[];
};

export function Gallery({
  home,
  mediaSections,
  section,
  onDismiss,
}: GalleryProps) {
  const [visibleSection, setVisibleSection] = useState(
    // `!`: the gallery is only rendered once there is at least one section.
    section || mediaSections[0]!.id,
  );
  const [activeSection, setActiveSection] = useState(visibleSection);
  const [carouselSection, setCarouselSection] = useState<{
    media: string[];
    startIndex: number;
  } | null>(null);
  const [sectionRefs, setSectionRefs] = useState<
    Record<string, React.RefObject<HTMLDivElement>>
  >({});
  const { basicInfo, listingInfo } = home;
  const { isDemoUnit, isAvailableForRent, isComingSoon, rentAmount } =
    listingInfo || {};

  // Coming Soon homes have pricing already set and published, even though
  // isAvailableForRent stays false until Full Listing Published (LOVE-335).
  const shouldShowRentAmount =
    !isDemoUnit && !!(isAvailableForRent || isComingSoon) && rentAmount;

  const categoryToDisplayFeats = useMemo(
    () => processHomeFeatures(home),
    [home],
  );

  const mediaFeaturesSections = useMemo(
    () =>
      Object.keys(categoryToDisplayFeats).reduce<MediaFeatureSection[]>(
        (acc, category) => {
          const featuresWithMedia = getMediaFeatures(
            categoryToDisplayFeats,
            category,
          );
          if (featuresWithMedia.length > 0) {
            const categoryMedia = {
              id: category,
              type: category,
              media: featuresWithMedia.reduce<FeatureMedia[]>(
                (acc, feature) => [
                  ...acc,
                  ...feature.media.map((media) => ({ ...media })),
                ],
                [],
              ),
            };
            return [...acc, categoryMedia];
          }
          return acc;
        },
        [],
      ),
    [categoryToDisplayFeats],
  );

  if (Object.keys(sectionRefs).length === 0) {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    const sections = [...mediaSections, ...mediaFeaturesSections];
    sections.forEach((section) => {
      refs[section.id] = createRef();
    });

    setSectionRefs(refs);
  }

  const allMedia = useMemo(() => {
    const seen = new Set<string>();
    return (
      [...mediaSections, ...mediaFeaturesSections]
        .flatMap((s) =>
          [...s.media]
            // `!`: `sortOrder` is optional on `MediaModel` only; asserting keeps
            // the comparator's existing behaviour for media that lack one.
            .sort((a, b) => a.sortOrder! - b.sortOrder!)
            .map((m) => m.url),
        )
        // Explicit predicate: the body has several returns, so TS can't infer one,
        // and every consumer of `allMedia` wants a plain `string`.
        .filter((url): url is string => {
          if (!url) return false;
          const key = dedupKey(url);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
    );
  }, [mediaSections, mediaFeaturesSections]);

  const onImageClick = useCallback(
    (image: string) => {
      const key = dedupKey(image);
      const found = allMedia.findIndex((url) => dedupKey(url) === key);
      setCarouselSection({
        media: allMedia,
        startIndex: found >= 0 ? found : 0,
      });
    },
    [allMedia],
  );

  useEffect(() => {
    // TODO: Seems like when first mounted, the refs are not fully set yet, so we need to wait for the next frame
    // useLayoutEffect didn't work either, might be because of the "setSectionRefs above"
    setTimeout(() => {
      // `!` on the lookup, `?.` on `current`: mirrors what this already did —
      // it read `.current` off the entry unconditionally and only guarded the
      // ref's contents.
      sectionRefs[activeSection]!.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 100);
  }, [activeSection, sectionRefs]);

  return (
    <ModalBase
      name="gallery"
      isOpen
      onDismiss={onDismiss}
      overlayClassName="bg-white"
      contentClassName="bg-white w-full"
    >
      <div
        className="fixed left-0 top-0 z-10 w-full border-0 border-b border-solid border-gray bg-white"
        style={{ height: 70 }}
      >
        <div className="container flex h-full items-center justify-between">
          <Dropdown
            name="sections"
            value={visibleSection}
            options={[...mediaSections, ...mediaFeaturesSections].map(
              (section) => ({
                // `.join('')` is not a rendering change: `getSection` returns
                // an array of strings, and React already concatenated it with
                // no separator when it was handed to `label` (typed `string`).
                label: getSection(section.type).join(''),
                value: section.id,
              }),
            )}
            onChange={setActiveSection}
          />
          <div className="flex items-center">
            <Text as="div" fontWeight="semibold" className="hidden lg:contents">
              <BulletList
                wrap
                items={[
                  // `!`: this is the house page's own modal, which already
                  // dereferences `basicInfo` unconditionally on the line below.
                  getFormattedBeds(basicInfo!),
                  getBathRoomString(basicInfo!),
                  <span key="sqft">
                    {formatNumber(basicInfo!.areaInSquareFeet!)} ft<sup>2</sup>
                  </span>,
                  shouldShowRentAmount ? (
                    <RentAmount rentAmount={rentAmount} />
                  ) : null,
                ]}
              />
            </Text>
            <ApplyButton
              homeId={home.basicInfo?.unitId}
              listingId={home.listingInfo?.uniqueId}
              isDemoUnit={home.listingInfo?.isDemoUnit}
              isAvailableForRent={home.listingInfo?.isAvailableForRent}
              isComingSoon={home.listingInfo?.isComingSoon}
              availabilityStatus={home.listingInfo?.availabilityStatus}
              size="small"
              className="ml-sm mr-lg h-xl w-3xl lg:mr-0"
            />
            <div className="ml-0 mr-2sm size-2sm cursor-pointer lg:ml-md">
              <IconClose onClick={onDismiss} />
            </div>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="container mb-3xl">
        {mediaSections.map((section) => (
          <>
            <Section
              reference={sectionRefs[section.id]!}
              key={section.id}
              setVisible={setVisibleSection}
              id={section.id}
            >
              <div className="relative mt-3xl">
                {/* when scrolling, the sticky bar at the top makes the title be hidden behind it
                    this dummy div is positioned higher than the section to offset the title */}
                <div
                  className="pointer-events-none absolute -top-3xl size-full"
                  ref={sectionRefs[section.id]!}
                />
                <Text fontWeight="semibold" variant="h2">
                  {section.type}
                </Text>
                <div className="mt-lg">
                  {
                    <GalleryGrid
                      onImageClick={onImageClick}
                      mediaImages={section.media}
                    />
                  }
                </div>
              </div>
            </Section>
          </>
        ))}
        {mediaFeaturesSections.map((section) => (
          <Section
            reference={sectionRefs[section.id]!}
            key={section.id}
            setVisible={setVisibleSection}
            id={section.id}
          >
            <div key={section.id} className="relative mt-3xl">
              <div
                className="pointer-events-none absolute -top-3xl size-full"
                ref={sectionRefs[section.id]!}
              />
              <Text fontWeight="semibold" variant="h2">
                {section.type}
              </Text>
              <div className="mt-lg">
                <div className="grid grid-cols-2 gap-2sm lg:grid-cols-3">
                  {section.media.map((image) => {
                    const isCommunal = image.isCommunal;
                    const imageName = image.imageName;

                    return (
                      <div
                        // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
                        // `MediaModel` has `mediaId`, not `id`, so this key has
                        // always been `undefined` and React has been falling
                        // back to the array index for this list. Suppressed
                        // rather than corrected, because changing a key changes
                        // list reconciliation.
                        key={image.id}
                        role="button"
                        tabIndex={0}
                        className={clsx(
                          'relative h-5xl cursor-pointer border border-solid border-gray',
                          styles.section,
                        )}
                        onClick={() => onImageClick(image.url!)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onImageClick(image.url!);
                          }
                        }}
                      >
                        {!!isCommunal && (
                          <div className="absolute left-xs top-xs rounded-small bg-black px-xs py-2xs text-white lg:left-sm lg:top-sm">
                            <Text fontWeight="semibold" variant="p1">
                              Communal
                            </Text>
                          </div>
                        )}

                        <Image
                          // `!` on `url` and `?? ''` on `alt`: both are
                          // optional on `MediaModel`, and the name is already
                          // rendered as visible text below this image.
                          src={image.url!}
                          alt={imageName ?? ''}
                          className="cursor-pointer object-cover"
                        />
                        <div
                          className="absolute bottom-0 flex w-full items-end justify-end"
                          style={{
                            height: 93,
                            background:
                              'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(13, 13, 13, 0.42) 52.86%, rgba(0, 0, 0, 0.52) 100%)',
                          }}
                        >
                          <Text
                            fontWeight="semibold"
                            className="mb-xs p-xs text-p1 text-white lg:mb-2sm lg:text-body"
                          >
                            {imageName}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Section>
        ))}
      </div>
      {carouselSection && (
        <GalleryCarousel
          media={carouselSection.media}
          startIndex={carouselSection.startIndex}
          onDismiss={() => setCarouselSection(null)}
        />
      )}
    </ModalBase>
  );
}

type SectionProps = {
  children: ReactElement;
  id: string;
  setVisible: (section: string) => void;
  reference: React.RefObject<HTMLElement>;
};

function Section({ children, id, setVisible, reference }: SectionProps) {
  const isOnScreen = useOnScreen(reference, '0px', 0.4);
  const previousIsOnScreen = useRef(isOnScreen);

  useEffect(() => {
    if (isOnScreen && !previousIsOnScreen.current) {
      setVisible(id);
    }
    previousIsOnScreen.current = isOnScreen;
  }, [isOnScreen, setVisible, id]);

  return <>{children}</>;
}
